import type { Message } from "@ag-ui/client";
import { isHitlTool } from "./hitlTools";
import type { ToolCallEntry, Turn } from "./useTurns";

// Un-resulted tool calls on settled (non-running, non-cancelled) assistant
// turns. The run ended without finishing its work — either a HITL pause or an
// interrupted run, which `Chat` resolves by reconciling against `/connect`.
export function danglingToolCalls(turns: readonly Turn[]): ToolCallEntry[] {
  const calls: ToolCallEntry[] = [];
  for (const turn of turns) {
    if (turn.kind !== "assistant" || turn.isRunning || turn.cancelled) continue;
    calls.push(...turn.toolCalls.filter((tc) => tc.result === undefined));
  }
  return calls;
}

// Dangling calls that are genuine HITL pauses: only the hardcoded HITL tools
// (see hitlTools.ts) can pause a run for input, so a dangling call from any
// other tool never blocks the composer or renders an answer card.
export function hitlPauseIds(calls: readonly ToolCallEntry[]): ReadonlySet<string> {
  return new Set(calls.filter((tc) => isHitlTool(tc.name)).map((tc) => tc.id));
}

// HITL tool-call ids in the raw AG-UI message list with no matching tool
// result yet. Read synchronously right after staging an answer (before React
// recomputes the derived turns) to decide whether a deferred run may resume: it
// must not resume while any paused HITL call is still unanswered, or that
// sibling strands — the backend backfills it as an interrupted tool-return and
// it can never be approved, which is what leaves an approval card stuck. The
// model can emit several HITL calls in one response (e.g. two `confirm_ingest`
// for two files), so this mirrors danglingToolCalls + hitlPauseIds at the
// message level to gate the resume on the whole set being answered.
export function unansweredHitlCallIds(messages: readonly Message[]): Set<string> {
  const resulted = new Set<string>();
  const hitlCallIds = new Set<string>();
  for (const message of messages) {
    if (message.role === "assistant") {
      for (const call of message.toolCalls ?? []) {
        if (isHitlTool(call.function.name)) hitlCallIds.add(call.id);
      }
    } else if (message.role === "tool") {
      resulted.add(message.toolCallId);
    }
  }
  return new Set([...hitlCallIds].filter((id) => !resulted.has(id)));
}
